/**
 * One field of a question card, drawn from its value, its error and a change callback alone
 * (cq-026, LEA-169). `AnswerForm` (`work-pane.tsx`) keeps the state, the save, the uploads and
 * every request; nothing here reads the plan or the server, so a field renders and is tested alone.
 *
 * Each kind reports a change in the shape its own inputs already produced: a partial draft for the
 * Standing answer fields, an updater for the slots (`availability-slots.tsx`), which the parent
 * applies to its own state.
 */
import { appDate } from "../../src/app-dates.ts";
import {
  type AvailabilityDraft,
  AvailabilityFields,
  type CardLength,
} from "../availability-slots.tsx";
import { DateField } from "../date-time-fields.tsx";
import { type Draft, AnswerFields as StandingAnswerFields } from "../faq.tsx";
import { HoursQuestionFields } from "../hours-question.tsx";

export type Decision = "" | "yes" | "no" | "need_to_check";
interface DecisionValue {
  readonly decision: Decision;
  readonly checker: string;
  readonly followUpOn: string;
}
export type LengthChoice = "minutes" | "omit";
/** What a meeting-length field reports: the radio chosen, or what was typed into the minutes. */
type DurationChange = { readonly choice: LengthChoice } | { readonly fields: Partial<Draft> };

const questionDecisionChoices: ReadonlyArray<{
  value: Exclude<Decision, "">;
  label: string;
}> = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "need_to_check", label: "Need to check" },
];

export type AnswerFieldProps =
  | {
      readonly kind: "decision";
      readonly idPrefix: string;
      readonly prompt: string;
      /** An earlier seller reply the answer finder matched, shown for the rep to confirm. */
      readonly suggestion: {
        readonly decision: "yes" | "no";
        readonly quote: string;
        readonly authoredAt: string;
      } | null;
      readonly value: DecisionValue;
      readonly onChange: (next: DecisionValue) => void;
    }
  | {
      readonly kind: "duration";
      readonly idPrefix: string;
      readonly prompt: string | undefined;
      readonly value: { readonly choice: LengthChoice; readonly draft: Draft };
      readonly disabled: boolean;
      readonly onChange: (change: DurationChange) => void;
    }
  | {
      readonly kind: "scheduling";
      readonly idPrefix: string;
      readonly value: Draft;
      readonly disabled: boolean;
      readonly onChange: (next: Partial<Draft>) => void;
    }
  | {
      readonly kind: "availability";
      readonly idPrefix: string;
      readonly prompt: string | undefined;
      readonly value: AvailabilityDraft;
      readonly length: CardLength;
      readonly onChange: (update: (current: AvailabilityDraft) => AvailabilityDraft) => void;
    }
  | {
      readonly kind: "text";
      /** The question's key, which is also the textarea's name. */
      readonly name: string;
      readonly idPrefix: string;
      readonly prompt: string;
      readonly value: string;
      /** False when an upload may stand in for typing (`uploadAllowed` with a file attached). */
      readonly required: boolean;
      /** Why the typed value cannot be used yet, said under the field; null when it can. */
      readonly error: string | null;
      readonly onChange: (next: string) => void;
    };

export function AnswerField(props: AnswerFieldProps) {
  switch (props.kind) {
    case "decision": {
      const { value, onChange, suggestion } = props;
      return (
        <fieldset className="lcr-answer-decision">
          <legend>{props.prompt}</legend>
          {suggestion ? (
            <aside
              className="lcr-answer-finder"
              aria-label="Suggested answer from campaign history"
            >
              <div>
                <strong>Suggested: {suggestion.decision === "yes" ? "Yes" : "No"}</strong>
                <span className="lcr-answer-finder-meta">
                  Earlier seller reply · {appDate(suggestion.authoredAt)}
                </span>
              </div>
              <blockquote>{suggestion.quote}</blockquote>
              <p>Review the source, then confirm or choose another answer.</p>
            </aside>
          ) : (
            <p>Choose the answer the lead should receive.</p>
          )}
          <div className="lcr-answer-options">
            {questionDecisionChoices.map(({ value: choice, label }) => (
              <label key={choice} className="lcr-answer-option">
                <input
                  type="radio"
                  name="decision"
                  value={choice}
                  checked={value.decision === choice}
                  onChange={() => onChange({ ...value, decision: choice })}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          {value.decision === "need_to_check" && (
            <div className="lcr-promise-fields">
              <label>
                Who is checking?
                <input
                  name="checker"
                  required
                  value={value.checker}
                  placeholder="e.g. the client"
                  onChange={(event) => onChange({ ...value, checker: event.target.value })}
                />
              </label>
              <div className="lcr-entry-slot">
                <label htmlFor={`${props.idPrefix}-follow-up`}>Follow up by</label>
                <DateField
                  id={`${props.idPrefix}-follow-up`}
                  name="follow-up-on"
                  required
                  notPast
                  value={value.followUpOn}
                  onChange={(followUpOn) => onChange({ ...value, followUpOn })}
                />
              </div>
              <p className="lcr-answer-note">
                The reply will name this question and date, answer everything else, and still ask
                for the campaign meeting.
              </p>
            </div>
          )}
        </fieldset>
      );
    }
    case "duration": {
      const { value, onChange } = props;
      return (
        <div className="lcr-answer-typed">
          <p>{props.prompt}</p>
          <label className="lcr-answer-choice">
            <input
              type="radio"
              name="length-choice"
              checked={value.choice === "minutes"}
              onChange={() => onChange({ choice: "minutes" })}
            />{" "}
            State a length
          </label>
          {value.choice === "minutes" && (
            <StandingAnswerFields
              shape="number"
              draft={value.draft}
              disabled={props.disabled}
              idPrefix={`${props.idPrefix}-duration`}
              onChange={(fields) => onChange({ fields })}
            />
          )}
          <label className="lcr-answer-choice">
            <input
              type="radio"
              name="length-choice"
              checked={value.choice === "omit"}
              onChange={() => onChange({ choice: "omit" })}
            />{" "}
            Don’t state a length
          </label>
        </div>
      );
    }
    case "scheduling":
      return (
        <HoursQuestionFields
          draft={props.value}
          disabled={props.disabled}
          idPrefix={`${props.idPrefix}-scheduling`}
          onChange={props.onChange}
        />
      );
    case "availability":
      return (
        <AvailabilityFields
          idPrefix={props.idPrefix}
          prompt={props.prompt}
          draft={props.value}
          onChange={props.onChange}
          length={props.length}
        />
      );
    case "text": {
      const hintId = `${props.idPrefix}-hint`;
      return (
        <>
          <label>
            {props.prompt}
            <textarea
              name={props.name}
              required={props.required}
              value={props.value}
              aria-invalid={props.error ? true : undefined}
              aria-describedby={props.error ? hintId : undefined}
              onChange={(event) => props.onChange(event.target.value)}
            />
          </label>
          {props.error && (
            <p className="lcr-answer-hint" id={hintId} role="status">
              {props.error}
            </p>
          )}
        </>
      );
    }
  }
}
